import axios from 'axios';
import FormData from 'form-data';
import Stripe from 'stripe';

import {
    ApiVoicesResponse,
    AvatarStatus,
    FaceSquare,
    ICouponData,
    IDreamsResponse,
    IDreamsTokens,
    ImageType,
    IShareRecord,
    IUserSettingsData,
    IUserSettingsRequest,
    IVoice,
    LibraryItem,
    PlanGroup,
    Presenter,
    PriceMetadata,
    ProductMetadata,
    ProductType,
    TtsInitResponse,
    UploadsResponse,
    VideoType,
} from '@d-id/types';

import { studioApiUrl } from '@/environment';
import { authenticatedAxios } from '@/services/auth';

import { mapVoices } from '@/services/api/voices';
import { FilterBy, ORDER, SORT_BY } from '@/state/video-library/videoLibrary';
import { logger } from '../logger';

export type Billing = 'month' | 'year';

export const getAzureToken = async (): Promise<TtsInitResponse> => {
    const response = await authenticatedAxios.get(`${studioApiUrl}/create/tts/init/v2`);
    return response.data;
};

export const getGoogleToken = async (): Promise<{ googleToken: string }> => {
    const response = await authenticatedAxios.get(`${studioApiUrl}/create/tts/googleinit/`);

    return response.data;
};

export const updateSubscription = async ({ priceId, coupon }: { priceId: string; coupon?: string }): Promise<{ credits?: number }> => {
    const response = await authenticatedAxios.put(`${studioApiUrl}/subscriptions/checkout`, { price_id: priceId, coupon });
    return response.data;
};

export const cancelSubscription = async (): Promise<{ invoiceData: Stripe.Invoice }> => {
    const response = await authenticatedAxios.put(`${studioApiUrl}/subscriptions/cancel`);
    return response.data;
};

export const getNextInvoice = async (): Promise<{ invoice: Stripe.Invoice }> => {
    const response = await authenticatedAxios.get(`${studioApiUrl}/subscriptions/next`);
    return response.data;
};

export const getSubscription = async (): Promise<{ subscription: any }> => {
    const response = await authenticatedAxios.get(`${studioApiUrl}/subscriptions/subscription`);
    return response.data;
};

export const getCurrentSubscription = async (): Promise<{ subscription: Stripe.Subscription | undefined }> => {
    const response = await authenticatedAxios.get(`${studioApiUrl}/subscriptions/current_subscription`);
    return response.data;
};

export const goToStripeCheckoutSubscription = async ({
    priceId,
    planGroup,
    billing,
}: {
    priceId: string;
    planGroup: PlanGroup;
    billing: Billing;
}) => {
    const response = await authenticatedAxios.post<{ url: string }>(`${studioApiUrl}/subscriptions/checkout`, {
        price_id: priceId,
        plan_group: planGroup,
        billing,
        base_url: window.location.origin,
    });

    return response.data;
};

type Product = {
    id: string;
    name: ProductType;
    images: string[];
    default_price: {
        id: string;
        unit_amount: number;
    };
    metadata: ProductMetadata;
};

export const getProducts = async (): Promise<{ products: Product[] }> => {
    const response = await authenticatedAxios.get(`${studioApiUrl}/subscriptions/products/`);
    return response.data;
};

export const getPrice = async (priceId: string): Promise<{ price: Stripe.Price }> => {
    const response = await authenticatedAxios.get(`${studioApiUrl}/subscriptions/prices/${priceId}`);
    return response.data;
};

export interface ProductPrice {
    id: string;
    unit_amount: number;
    type: 'one_time' | 'recurring';
    metadata: PriceMetadata;
    product?: { default_price: string };
    nickname?: string;
}

export const getProductPrices = async ({
    productId,
    oneTime,
    interval,
}: {
    productId: string;
    oneTime?: boolean;
    interval?: Billing;
}): Promise<{ prices: ProductPrice[] }> => {
    if (!productId) {
        return { prices: [] };
    }
    const pricesUrl = new URL(`${studioApiUrl}/subscriptions/products/${productId}/prices`);
    if (oneTime) pricesUrl.searchParams.append('one_time', 'true');
    if (interval) pricesUrl.searchParams.append('interval', interval);
    const response = await authenticatedAxios.get(pricesUrl.toString());
    return response.data;
};

export const getDataForChangePlan = async (newPriceId: string, coupon?: string) => {
    logger.debug(`getDataForChangePlan with newPriceId: ${newPriceId}`);
    const getDataForChangePlanUrl = new URL(`${studioApiUrl}/subscriptions/change/${newPriceId}`);

    if (coupon) {
        getDataForChangePlanUrl.searchParams.append('coupon', coupon);
    }

    const response = await authenticatedAxios.get<{
        upcomingInvoiceData: Stripe.Invoice;
        currentPeriodEnd: number;
        prevPrice: Stripe.Price;
        creditCardDetails?: Stripe.PaymentMethod.Card;
    }>(getDataForChangePlanUrl.toString());

    return response.data;
};

export const getStripeCustomerPortalUrl = async () => {
    const response = await authenticatedAxios.get(`${studioApiUrl}/subscriptions/settings`);
    return response;
};

export const getChurnKey = async () => {
    const response = await authenticatedAxios.get(`${studioApiUrl}/subscriptions/churnkey`);
    return response.data.churnkey;
};

export const getRecaptchaSiteKey = async () => {
    const response = await authenticatedAxios.get(`${studioApiUrl}/create/get-recaptcha-site-key`);
    return response.data.siteKey;
};

export const getRecaptchaScore = async (token: string) => {
    const response = await authenticatedAxios.get<{ score: number }>(`${studioApiUrl}/create/get-recaptcha-score/${token}`);
    return response.data.score;
};

export const getCouponDetails = async (promoCode: string, priceId: string) => {
    const response = await authenticatedAxios.get<{ coupon: ICouponData['coupon']; attempts: number }>(
        `${studioApiUrl}/subscriptions/coupon?promo=${promoCode}&price_id=${priceId}`
    );
    return response.data;
};

export type GetPresentersByStatus = AvatarStatus | 'all';
// By default, get presenters endpoint will return ONLY presenters with status Done
export const getPresenters =
    ({
        type,
        isAnonymous,
        status,
        mixpanel,
        includeExpressive,
    }: {
        type: ImageType;
        isAnonymous?: boolean;
        status?: GetPresentersByStatus;
        mixpanel?: any;
        includeExpressive?: boolean;
    }) =>
    async (): Promise<Presenter[]> => {
        const baseUrl = `${studioApiUrl}/create/images${isAnonymous ? '/anonymous' : ''}`;
        const searchParams = new URLSearchParams({
            show: 'true',
            type: type.toString(),
        });

        if (status) {
            searchParams.append('status', status);
        }

        if (includeExpressive) {
            searchParams.append('includeExpressive', 'true');
        }

        const presentersResponse = await authenticatedAxios.get(`${baseUrl}?${searchParams.toString()}`);

        /**
         * Followed by a bug that was fixed in Azure Face Services
         * Face detections gave non-sensible coordinates (above 10,000)
         * We temporarily re-detect faces for presenters created during bug existed
         */
        const fixedPresenters = [...presentersResponse.data];
        let faceDetectionRedoSuccessCount = 0;
        let faceDetectionRedoFailureCount = 0;
        for (let i = 0; i < fixedPresenters.length; i++) {
            const presenter = fixedPresenters[i];
            const { id, poster, type, faceRect } = presenter;
            if (
                type === VideoType.TALK &&
                faceRect &&
                Object.values(faceRect).some(coordinate => Math.abs(coordinate as number) > 10_000)
            ) {
                try {
                    const { faces } = await updateImageFaceDetection(id, poster);
                    if (!faces?.length) {
                        faceDetectionRedoFailureCount++;
                        logger.error('Face not detected during migration', { presenter });
                        continue;
                    }
                    fixedPresenters[i] = { ...presenter, faceRect: presenter?.faces?.[0]?.detection, faces };
                    faceDetectionRedoSuccessCount++;
                } catch (err) {
                    faceDetectionRedoFailureCount++;
                    logger.error('Failed to update face detection', { err, presenter });
                }
            }
        }

        if (faceDetectionRedoSuccessCount > 0 || faceDetectionRedoFailureCount > 0) {
            mixpanel?.track('face-detection-redo', {
                updatedPresenterCount: faceDetectionRedoSuccessCount,
                failedToUpdatePresenterCount: faceDetectionRedoFailureCount,
            });
        }

        return fixedPresenters;
    };

export const uploadImage = async ({ image, metadata }: { image: File; metadata?: Record<string, string> }) => {
    try {
        const result = await uploadImageToPresignedUrl({ image, metadata });
        return result;
    } catch (err: any) {
        if (err?.response?.data?.kind || err?.response?.data?.description) {
            throw err;
        }
        return uploadImageFromBuffer({ image, metadata });
    }
};

const uploadImageFromBuffer = async ({ image, metadata }: { image: File; metadata?: Record<string, string> }) => {
    const imagesFormData = new FormData();
    imagesFormData.append('image', image);
    imagesFormData.append('content_type', image.type);
    if (metadata) {
        imagesFormData.append('metadata', JSON.stringify(metadata));
    }

    const imagesResponse = await authenticatedAxios.post(`${studioApiUrl}/create/images`, imagesFormData, {
        headers: { 'Content-Type': `multipart/form-data` },
    });

    return imagesResponse.data;
};

const uploadImageToPresignedUrl = async ({ image, metadata }: { image: File; metadata?: Record<string, string> }) => {
    const sourceUrl = await uploadFile(image);
    const imagesFormData = new FormData();
    imagesFormData.append('source_url', sourceUrl);
    imagesFormData.append('content_type', image.type);
    if (metadata) {
        imagesFormData.append('metadata', JSON.stringify(metadata));
    }

    const imagesResponse = await authenticatedAxios.post(`${studioApiUrl}/create/images`, imagesFormData, {
        headers: { 'Content-Type': `multipart/form-data` },
    });

    return imagesResponse.data;
};

export const deleteImage = async (imageId: string) => {
    const response = await authenticatedAxios.delete(`${studioApiUrl}/create/images/${imageId}`);
    return response.data;
};

export const updateImage = async ({
    id,
    show,
    createdAt: created_at,
    presenterType: presenter_type,
    name,
}: {
    id: string;
    show: boolean;
    createdAt: string;
    presenterType: ImageType;
    name?: string;
    faces?: FaceSquare[];
}) => {
    const response = await authenticatedAxios.put(`${studioApiUrl}/create/images`, {
        id,
        show,
        created_at,
        presenter_type,
        name,
    });

    return response.data;
};

export const getReadmeioToken = async (apiKey: string): Promise<string> => {
    const response = await authenticatedAxios.post(`${studioApiUrl}/create/readmeio`, { apiKey });
    return response.data;
};

export const createShareRecord = async (video: LibraryItem): Promise<{ id: string }> => {
    const { id: video_id, name: title = 'New creative video', video_url: result_url, thumbnail_url } = video;
    const response = await authenticatedAxios.post(`${studioApiUrl}/create/share`, {
        video_id,
        title,
        poster: thumbnail_url,
        video: result_url,
        subtitles_url: video.subtitles_url,
    });
    return response.data;
};

export const getShareRecord = async (id: string): Promise<IShareRecord> => {
    const response = await authenticatedAxios.get(`${studioApiUrl}/create/share/${id}`);
    if (response.status === 200 && response.data) {
        return response.data;
    } else {
        return Promise.reject(response.status);
    }
};

export const createDream = async (userPrompt: string, prompt: string): Promise<IDreamsResponse> => {
    const response = await authenticatedAxios.post(`${studioApiUrl}/create/dreams`, { userPrompt, prompt });
    return response.data;
};

export const getDreamsTokens = async (): Promise<IDreamsTokens> => {
    const response = await authenticatedAxios.get(`${studioApiUrl}/create/dreams/tokens`);
    return response.data;
};

export type GetLibraryItemsResponse = {
    items: LibraryItem[];
    shouldPoll: boolean;
    token?: string;
};

type QueryParams = {
    order?: ORDER;
    sort_by?: SORT_BY;
    filter_by?: FilterBy;
    created_by?: string;
    token?: string;
    includeExternalProjects?: boolean;
};

const DEFAULT_PROJECTS_LIMIT = 15;
export const getProjectLibraryItems = async (isAnonymous: boolean, queryParams: QueryParams = {}): Promise<GetLibraryItemsResponse> => {
    if (isAnonymous) {
        return { items: [], shouldPoll: false };
    }
    const response = await authenticatedAxios.get(`${studioApiUrl}/create/libraryItems/projects`, {
        params: { limit: DEFAULT_PROJECTS_LIMIT, ...queryParams },
    });
    return response.data;
};

const isSuccess = (status: number) => [200, 201, 204].includes(status);

export const uploadFile = async (file: File, signal?: AbortSignal, options?: { s3Uri?: boolean }) => {
    try {
        const uploadsResponse = await authenticatedAxios.post(`${studioApiUrl}/create/uploads`, {
            name: file.name.replaceAll(/\s+/g, '-'),
            type: file.type,
        });
        const { url, key, fields } = uploadsResponse.data as UploadsResponse;

        const response = await axios.post(url, { key, ...fields, file }, { signal, headers: { 'Content-Type': `multipart/form-data` } });
        if (!(isSuccess(response.status) && isSuccess(uploadsResponse.status))) {
            logger.error('failed to upload to presigned url', { response, uploadsResponse });
        }

        if (options?.s3Uri) {
            return `s3://${fields.bucket}/${key}`;
        }

        return `${url}/${key}`;
    } catch (err) {
        logger.error('Failed to upload file', { err, errJson: JSON.stringify(err) });
        throw err;
    }
};

export const updateImageFaceDetection = async (uploadId: string, sourceUrl: string): Promise<{ faces: FaceSquare[] }> => {
    const response = await authenticatedAxios.post(`${studioApiUrl}/create/images/update-face-detection`, {
        upload_id: uploadId,
        source_url: sourceUrl,
    });
    return response.data;
};

export const getAllVoicesAnonymous = async (): Promise<IVoice[]> => {
    const response = await authenticatedAxios?.get<{ voices: ApiVoicesResponse }>(`${studioApiUrl}/create/tts/voices/anonymous`);
    return mapVoices(response?.data?.voices);
};

export const useCampaign = async (campaignId: string) => {
    const response = await authenticatedAxios.post(`${studioApiUrl}/create/campaign`, {
        campaignId,
    });

    return response?.data;
};

export const createPolotnoLayers = async (projectId: string, rawData: any, controller: AbortController) => {
    const response = await authenticatedAxios?.post<{ layers: { background: string; overlay: string }[] }>(
        `${studioApiUrl}/create/layering`,
        {
            project_id: projectId,
            data: rawData,
        },
        { signal: controller.signal }
    );

    return response?.data?.layers;
};

export const deleteTtsCache = ({ projectId, sceneId }: { projectId: string; sceneId: string }, signal?: AbortSignal) => {
    return authenticatedAxios.delete(`${studioApiUrl}/create/uploads/projectAudio/${projectId}/scenes/${sceneId}`, { signal });
};

export const getUserSettings = async (): Promise<IUserSettingsData | undefined> => {
    const response = await authenticatedAxios.get(`${studioApiUrl}/create/user-settings`);
    return response.data;
};

export const setUserSettings = async (settings: IUserSettingsData, override?: boolean): Promise<IUserSettingsData> => {
    const response = await authenticatedAxios.post(`${studioApiUrl}/create/user-settings`, {
        settings,
        override,
    } as IUserSettingsRequest);
    return response.data;
};

export const getFreeVideos = async (): Promise<{ limit: number; remaining: number; count: number }> => {
    const response = await authenticatedAxios.get(`${studioApiUrl}/create/user-settings/free-videos`);
    return response.data;
};

export const incrementFreeVideosUsage = async ({ count }: { count: number }) => {
    const response = await authenticatedAxios.post(`${studioApiUrl}/create/user-settings/free-videos`, { count });
    return response.data;
};
